import { DealStatusTypeEnum } from '@zaoblako/shared/modules/business/enum/deal-status-type.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { BusinessDtoKeysEnum } from '@zaoblako/shared/modules/business/enum/business-dto-keys.enum';

import * as yup from 'yup';

/** Deal status response body - DTO signature */
export interface DealStatusFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;

  // Tech columns
  /** Deal status creation date and time */
  created: Date;
  /** Deal status last update date and time */
  updated: Date;
  /** Deal status soft deletion date and time */
  deleted?: Date;

  // Data columns
  /** Status display name */
  name: string;
  /** Display color */
  color?: string;
  /** If `true` then this status will be initial for the new deals */
  initial?: boolean;
  /** If `true` then deals with this status will be considered "active" */
  active?: boolean;
  /** Status type */
  type?: DealStatusTypeEnum;
  /** If `true` then status can be edited by user */
  editable?: boolean;
  /** If `true`, then the transition of the deal to this status should be accompanied by a comment */
  isCommentRequired?: boolean;

  // Relations
  /** Statuses to which a deal can be transferred from this status */
  transitions?: DealStatusFindInterface[];
}

/** Deal status response body - DTO schema */
export const DealStatusFindDto = SerdeDtoClass.create<DealStatusFindInterface>(BusinessDtoKeysEnum.DEAL_STATUS_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Обновлён',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Обновлён',
    serializer: yup.date(),
  },
  name: {
    sortable: true,
    label: 'Имя',
    serializer: yup.string(),
  },
  color: {
    optional: true,
    label: 'Цвет',
    serializer: yup.string(),
  },
  initial: {
    sortable: true,
    optional: true,
    label: 'Начальный',
    hint: 'Статус по-умолчанию для новых сделок. Допускается только один начальный статус на тенант',
    serializer: yup.boolean(),
  },
  active: {
    sortable: true,
    optional: true,
    label: 'Активный',
    serializer: yup.boolean(),
  },
  type: {
    label: 'Тип',
    hint: 'Внутренний системный тип статуса, определяет "вшитые" статусы, которые нельзя редактировать',
    serializer: yup
      .string()
      .oneOf(Object.values(DealStatusTypeEnum)),
  },
  editable: {
    sortable: true,
    optional: true,
    label: 'Редактируемый',
    hint: 'Если флаг выключен, то данный статус нельзя редактировать. Предназначен для предопределенных статусов.',
    serializer: yup.boolean(),
  },
  isCommentRequired: {
    sortable: true,
    optional: true,
    label: 'Требуется комментарий',
    hint: 'Если флаг включен, то при переходе сделки в данный статус требуется написать комментарий',
    serializer: yup.boolean(),
  },
  transitions: {
    optional: true,
    label: 'Переход в статусы',
    hint: 'Список статусов, в которые можно перевести сделку в этом статусе',
    nested: BusinessDtoKeysEnum.DEAL_STATUS_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
});
